// Private / Team switch (RFC-1371 D3).
//
// Visibility is an AXIS ACROSS the dashboard, not a tab. It was briefly a
// reserved "Team" tab, which was wrong: it made "shared with the team"
// mutually exclusive with the tabs a user has organised their work into, so a
// named tab could only ever show private projects and search silently spanned
// both halves. As a switch it composes — every tab, All and named alike,
// answers "the private half" or "the team half" of the same list, and search
// inherits the scope because it is the same query.
//
// Defaults to Private, and that default is load-bearing rather than cosmetic:
// the team half can contain work published by OTHER members, so opening the
// dashboard should show you your own things first, not a list you did not put
// there.
//
// Renders nothing when the feature is off, so the dashboard is byte-identical
// to pre-teams.

import { useTranslation } from 'react-i18next'

import { useActiveTeam } from '../../contexts/ActiveTeamContext'
import { useTeamsEnabled } from '../../features/teams/useTeamsEnabled'

export type ProjectVisibility = 'private' | 'team'
// A plain typed array rather than `as const`: this codebase forbids `as`
// entirely (check-forbidden-ts-syntax), and the union above is the real
// source of truth anyway, so the array cannot drift from it without tsc
// noticing.
export const VISIBILITIES: ProjectVisibility[] = ['private', 'team']
export const DEFAULT_VISIBILITY: ProjectVisibility = 'private'

/** Narrow an untrusted string (URL param) to a visibility, else the default. */
export function asVisibility(raw: string | null): ProjectVisibility {
  return raw === 'team' ? 'team' : DEFAULT_VISIBILITY
}

export default function VisibilitySwitch({
  value,
  onChange,
}: {
  value: ProjectVisibility
  onChange: (next: ProjectVisibility) => void
}) {
  const { t } = useTranslation('settings')
  const enabled = useTeamsEnabled()
  const { activeTeam, isLoading } = useActiveTeam()

  // TWO conditions, and the second is not belt-and-braces.
  //
  // The flag is GLOBAL (targetable=False), so it says the feature exists in
  // this environment — not that it exists for this account. An account that
  // predates the 092 trigger has no personal team and has not been backfilled,
  // so `activeTeam` is null, so the list sends no `team_id`, so the backend
  // sets `scoped_visibility = None` and IGNORES the filter. Rendering the
  // switch for them produces a control where both halves return the same list
  // and clicking does nothing — which is worse than absent, because it invites
  // the conclusion that Team is empty rather than inapplicable.
  //
  // Gating on the team rather than on a separate "migrated" flag keeps one
  // source of truth: the switch appears exactly when the backend will honour
  // it. When the RFC-1372 §7 backfill mints teams for these accounts, they get
  // the switch with no further change here.
  //
  // `isLoading` is included so it does not flash in and out on first paint
  // while the teams query settles.
  if (!enabled || isLoading || activeTeam === null) return null

  return (
    <div
      role="radiogroup"
      aria-label={t('visibilitySwitch.label')}
      data-testid="visibility-switch"
      className="inline-flex items-center rounded-md border border-border bg-surface-1 p-0.5"
    >
      {VISIBILITIES.map((option) => {
        const selected = option === value
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option)}
            data-testid={`visibility-${option}`}
            className={`px-3 py-1 text-xs font-medium uppercase tracking-wider rounded border transition-colors ${
              selected
                ? 'border-primary/40 bg-primary/15 text-foreground'
                : 'border-transparent text-text-muted hover:text-text-secondary'
            }`}
          >
            {t(option === 'private' ? 'visibilitySwitch.private' : 'visibilitySwitch.team')}
          </button>
        )
      })}
    </div>
  )
}
